/**
 * PanelBody
 * 
 * @Author: Focci
 * @Date: 2023-07-14 12:03:39
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-14 12:03:39
 */

import PropTypes from 'prop-types'
import clsx from 'clsx'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CloseIcon from '@mui/icons-material/Close'
import { ButtonBase, Checkbox } from '@comp/material'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef, useState } from 'react'
import { isFunction } from 'lodash-es'

export function Legend({
  data = {}
}) {
  const styles = {}
  data.fill && (styles.backgroundColor = data.fill)
  data.outline && (styles.border = `solid 1px ${data.outline}`)

  if(data?.type === 'fill-color') {
    return <div className="w-3 h-3" style={styles} />
  }
  if(data?.type === 'circle') {
    return <div className="w-3 h-3 rounded-full" style={styles} />
  }
  if(data?.type === 'line') {
    return <div className="w-3 h-1 rounded-full" style={styles} />
  }
  if(data?.type === 'fill-pattern') {
    return <div 
      className="w-3 h-3" 
      style={{ backgroundImage: `url(${data.fill})` }} 
    />
  }

  return (
    <div />
  )
}

function CloseButton({
  onClick = () => {}
}) {
  return (
    <div
      className="cursor-pointer"
      onKeyPress={onClick}
      onClick={onClick}
      role="button"
      tabIndex="0"
    >
      <CloseIcon className="size-4" />
    </div>
  )
}

function CollapsibleIcon({ expanded, ...props }) {
  return (
    <span
      {...props}
      className={clsx([
        'bg-black/80 rounded-full w-[.8rem] h-[.8rem] text-white',
        'flex items-center justify-center cursor-pointer',
        expanded && 'rotate-180'
      ])}
    >
      <KeyboardArrowDownIcon sx={{ fontSize: '.8rem' }} />
    </span>
  )
}

/**
 * Checkbox 选择项
 */
export function PanelCheckboxItem({
  data = {},
  legend = null,
  checked = false,
  onChange
}) {
  if(data?.type === 'subtitle') {
    return (
      <div className="my-2 font-medium text-f.7">
        {data.label}
      </div>
    )
  }

  return (
    <label className="flex items-center justify-between my-2">
      {legend && (
        <div className="flex shrink-0">
          <Legend data={legend} />
        </div>
      )}
      <div className="flex items-center flex-auto ml-2">
        {data.label}
      </div>
      {isFunction(onChange) && (
        <div>
          <Checkbox checked={checked} onChange={onChange} />
        </div>
      )}
    </label>
  )
}

function CollapsibleButton({
  expandName = '',
  value = true,
  onClose,
  ...props
}) {
  const ts = useTranslations('search')
  const ta = useTranslations('article')

  return (
    <div className="flex items-center bg-white">
      <ButtonBase className="w-full" {...props}>
        <div className="py-2.5 px-2 text-f.7 w-full bg-cf2 flex items-center justify-between gap-1">
          <span>{value ? ta('hide') : (expandName || ts('expandOptions'))}</span>
          <CollapsibleIcon expanded={value} />
        </div>
      </ButtonBase>
      {!value && (
        <div className="px-1">
          <CloseButton onClick={onClose} />
        </div>
      )}
    </div>
  )
}

/**
 * 面板骨架(左边菜单，右边内容)
 */
export function PanelScoff({
  suffix,
  collapsible = true, // 是否可折叠
  tabName = '',
  className = 'flex-auto w-52 md:w-56 pb-2 px-4',
  children,
  onClose,
  delay = 8000000
}) {
  const [expanded, setExpanded] = useState(true)
  const timer = useRef()

  const clearTimer = useCallback(() => {
    timer.current && clearTimeout(timer.current)
  }, [])

  const handleClose = useCallback(() => {
    isFunction(onClose) && onClose()
    clearTimer()
  }, [onClose, clearTimer])

  const handleExpand = useCallback(() => {
    setExpanded(!expanded)
    clearTimer()
  }, [clearTimer, expanded])

  useEffect(() => {
    setExpanded(true)
    timer.current = setTimeout(() => {
      setExpanded(false)
      clearTimer()
    }, delay)

    return () => clearTimer()
  }, [tabName, delay, clearTimer])

  return (
    <>
      <div className="flex bg-white">
        {expanded && suffix}
        <div 
          className={clsx([
            'flex flex-col bg-white relative custom-scroll-bar', 
            expanded ? 'h-full max-h-[14rem] overflow-y-auto' : 'h-0 hidden'
          ])}
        >
          <div className="sticky bg-white top-0 left-0 right-0 flex items-center justify-end gap-1 px-2 pt-2">
            {collapsible && <CollapsibleIcon expanded={true} onClick={handleExpand} />}
            <CloseButton onClick={handleClose} />
          </div>
          <div className={className}>
            {children}
          </div>
        </div>
      </div>
      {collapsible && !expanded && (
        <CollapsibleButton
          value={expanded}
          expandName={tabName}
          onClose={handleClose}
          onClick={handleExpand}
        />
      )}
    </>
  )
}

PanelScoff.propTypes = {
  collapsible: PropTypes.bool,
  children: PropTypes.node,
}

/**
 * 面板内容
 */
export default function PanelBody({
  children
}) {
  return (
    <div className="mt-3">
      <div className="bg-white rounded-md shadow-large overflow-hidden">
        {children}
      </div>
    </div>
  )
}

PanelBody.propTypes = {
  children: PropTypes.node,
}
